import { mediaSchema } from "@/features/dashboard/types/media";
import { z } from "zod";
import { caseActivityFormSchema } from "./activities";

const caseEvidenceSchema = z.object({
	id: z.string(),
	case_id: z.string(),
	case_record_user_id: z.string(),
	description: z.string().min(1, { message: "required" }),
	witness_name: z.string().optional(),
	exhibit: z.string().optional().nullable(),
	parent_id: z.string().optional().nullable(),
	parties: z.string(),
	purpose: z.string().min(1, { message: "required" }),
	evidence_type: z.string(),
	status: z.string().optional().nullable(),
	evidence: mediaSchema,
	caseActivity: caseActivityFormSchema.optional(),
	case_activity_id: z.string().optional().nullable(),
	case_activity_ids: z.array(z.string().optional().nullable()).optional(),
	type: z.string().optional(),
	stenography: z.union([mediaSchema, z.instanceof(File)]).optional(),
	affidavit: z.union([mediaSchema, z.instanceof(File)]).optional(),
	attachment_deleted: z.boolean().default(false),
	marking_status: z.string().nullable().optional(),
	admission_status: z.string().nullable().optional(),
	created_at: z.string(),
	category: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
			}),
		)
		.optional()
		.nullable(),
	shapes: z.any(),
	updated_at: z.string().optional(),
	expunged_at: z.string().optional(),
	updated_marking_status_at: z.string().optional(),
	updated_admission_status_at: z.string().optional(),
});

export const caseEvidenceCaseFilingSchema = z.object({
	id: z.string().optional().nullable(),
	case_record_user_id: z.string().optional(),
	case_id: z.string().optional(),
	witness_name: z.string().optional(),
	_id: z.string(),
	parent_id: z.string().nullable().optional(),
	evidence_type: z.string(),
	file: z.instanceof(File).nullable(),
	parties: z.string().optional(),
	status: z.string().nullable().optional(),
	evidence: z.union([mediaSchema, z.any()]),
	affidavit: z.union([mediaSchema, z.any()]),
	stenography: z.union([mediaSchema, z.any()]),
	exhibit: z.string().optional().nullable(),
	description: z.string().optional(),
	purpose: z.string().optional(),
	marking_status: z.string().nullable().optional(),
	admission_status: z.string().nullable().optional(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	expunged_at: z.string().optional(),
	updated_marking_status_at: z.string().optional(),
	updated_admission_status_at: z.string().optional(),
});

export const caseEvidenceFormSchema = z.object({
	id: z.string().optional(),
	case_id: z.string(),
	parent_id: z.string().optional().nullable(),
	witness_name: z.string().optional().nullable(),
	evidence_type: z.union([z.literal("Documentary"), z.literal("Object"), z.literal("Testimonial")]),
	parties: z.string(),
	case_record_user_id: z.string().optional().nullable(),
	status: z.string().nullable().optional(),
	marking_status: z.string().nullable().optional(),
	admission_status: z.string().nullable().optional(),
	evidence: z.union([mediaSchema, z.any()]),
	stenography: z.instanceof(File).nullable().optional(),
	affidavit: z.instanceof(File).nullable().optional(),
	exhibit: z.string().optional().nullable(),
	description: z.string().optional().nullable(),
	purpose: z.string().optional().nullable(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	expunged_at: z.string().optional(),
	case_activity_id: z.string().optional().nullable(),
	caseActivity: caseActivityFormSchema.optional(),
	category: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
			}),
		)
		.optional(),
	updated_marking_status_at: z.string().optional(),
	updated_admission_status_at: z.string().optional(),
});

export const evidenceStatusFormSchema = z.object({
	id: z.string().optional(),
	case_id: z.string(),
	evidence_type: z.union([z.literal("Documentary"), z.literal("Object"), z.literal("Testimonial")]).optional(),
	parties: z.string().optional(),
	case_record_user_id: z.string().optional(),
	status: z.string().nullable().optional(),
	marking_status: z.string().optional().nullable(),
	admission_status: z.string().nullable().optional(),
	evidence: z.union([mediaSchema, z.any()]),
	exhibit: z.string().optional().nullable(),
	description: z.string().optional().nullable(),
	purpose: z.string().optional().nullable(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	expunged_at: z.string().optional(),
	case_activity_id: z.string().optional().nullable(),
	category: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
			}),
		)
		.optional()
		.nullable(),
	caseActivity: caseActivityFormSchema.optional(),
	updated_marking_status_at: z.string().optional().nullable(),
	updated_admission_status_at: z.string().optional().nullable(),
});
export const markingStatusFormSchema = z.object({
	id: z.string().optional(),
	case_id: z.string(),
	evidence_type: z.union([z.literal("Documentary"), z.literal("Object"), z.literal("Testimonial")]).optional(),
	parties: z.string().optional(),
	case_record_user_id: z.string().optional(),
	status: z.string().nullable().optional(),
	updated_marking_status_at: z.string({ message: "Required" }),
	marking_status: z.string({ message: "Required" }),
	admission_status: z.string().nullable().optional(),
	evidence: z.union([mediaSchema, z.any()]),
	exhibit: z.string().optional().nullable(),
	description: z.string().optional().nullable(),
	purpose: z.string().optional().nullable(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	expunged_at: z.string().optional(),
	category: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
			}),
		)
		.optional()
		.nullable(),
	case_activity_id: z.string().optional().nullable(),
	caseActivity: caseActivityFormSchema.optional(),
	updated_admission_status_at: z.string().optional().nullable(),
});
export const admissionStatusFormSchema = z.object({
	id: z.string().optional(),
	case_id: z.string(),
	evidence_type: z.union([z.literal("Documentary"), z.literal("Object"), z.literal("Testimonial")]).optional(),
	parties: z.string().optional(),
	case_record_user_id: z.string().optional(),
	status: z.string().nullable().optional(),
	marking_status: z.string().nullable().optional(),
	updated_admission_status_at: z.string({ message: "Required" }),
	admission_status: z.string({ message: "Required" }),
	evidence: z.union([mediaSchema, z.any()]),
	exhibit: z.string().optional().nullable(),
	description: z.string().optional().nullable(),
	purpose: z.string().optional().nullable(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	expunged_at: z.string().optional(),
	case_activity_id: z.string().optional().nullable(),
	category: z
		.array(
			z.object({
				label: z.string().optional().nullable(),
				value: z.string().optional().nullable(),
			}),
		)
		.optional()
		.nullable(),
	caseActivity: caseActivityFormSchema.optional(),
	updated_marking_status_at: z.string().optional().nullable(),
});

export const caseTestimonialEvidenceSchema = z.object({
	id: z.string().optional(),
	case_id: z.string(),
	evidence_type: z.string(),
	parties: z.string(),
	case_record_user_id: z.string().optional().nullable(),
	status: z.string().nullable().optional(),
	witness_name: z.string().optional().nullable(),
	description: z.string().optional().nullable(),
	marking_status: z.string().nullable().optional(),
	admission_status: z.string().nullable().optional(),
	evidence: z.union([mediaSchema, z.any()]),
	stenography: z.instanceof(File).nullable().optional(),
	affidavit: z.instanceof(File).nullable().optional(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	expunged_at: z.string().optional(),
});

const evidenceNoteSchema = z.object({
	id: z.string(),
	case_evidence_id: z.string().nullable(),
	case_record_id: z.string().nullable(),
	case_record_user_id: z.string().nullable(),
	share_with: z.array(
		z.object({
			label: z.string(),
			value: z.string(),
		}),
	),
	note: z.string().nullable(),
	medias: z.array(z.any()).nullable(),
	created_by: z.string().nullable(),
	created_at: z.string(),
	updated_at: z.string().nullable(),
});

export const evidenceNoteFormSchema = z.object({
	id: z.string().nullable().optional(),
	case_evidence_id: z.string(),
	case_record_id: z.string().nullable(),
	note: z.string().min(1, "Note is required"),
	attachment: z.any().nullable(),
	share_with: z.any(),
});

export type CaseTestimonialEvidenceSchema = z.infer<typeof caseTestimonialEvidenceSchema>;
export type CaseEvidence = z.infer<typeof caseEvidenceSchema>;
export type CaseEvidenceForm = z.infer<typeof caseEvidenceFormSchema>;
export type EvidenceStatusForm = z.infer<typeof evidenceStatusFormSchema>;
export type MarkingStatusForm = z.infer<typeof markingStatusFormSchema>;
export type AdmissionStatusForm = z.infer<typeof admissionStatusFormSchema>;
export type EvidenceNote = z.infer<typeof evidenceNoteSchema>;
export type EvidenceNoteForm = z.infer<typeof evidenceNoteFormSchema>;
